/**
 * Purpose:
 * Centralize required headers for Python API calls in one place.
 *
 * Why this file exists:
 * - Avoid repeating header-building logic across multiple services.
 * - Keep Python endpoint behavior consistent and easier to maintain.
 * - Enforce a single source of truth for required Python headers.
 *
 * Scope:
 * - Applies ONLY to requests whose URL starts with the Python/data-extraction base URL.
 * - Adds the following headers:
 *   - `x-Client-Name`
 *   - `x-tenant-id`
 *   - `x-user`
 *   - `Authorization` (Bearer token)
 */
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggingService } from '@app/core/services';
import { AuthStore } from '@app/core/state/auth.store';
import { AppSettingsStore } from '@app/core/state/app-settings-store/app-settings-store';
import { TenantStateService } from '@app/core/services/tenant-state.service';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

const API_KEY_HEADER = 'X-Api-Key';

@Injectable()
export class PythonApiHeadersInterceptor implements HttpInterceptor {
    constructor(
        private readonly authStore: AuthStore,
        private readonly appSettingsStore: AppSettingsStore,
        private readonly tenantStateService: TenantStateService,
        private readonly loggingService: LoggingService
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const pythonApiRoot = environment.apiRootPython?.trim();

        // Safety: if Python API root is not configured, do not mutate any request.
        if (!pythonApiRoot) {
            return next.handle(req);
        }

        // Strict scope: only Python API root requests are handled by this interceptor.
        if (!req.url.startsWith(pythonApiRoot)) {
            return next.handle(req);
        }

        // API-key requests use a separate backend authentication scheme and
        // must not be mixed with user Bearer or identity headers.
        if (req.headers.has(API_KEY_HEADER)) {
            return next.handle(req);
        }

        const settings = this.appSettingsStore.settings();
        const clientName =
            settings?.user?.clientName?.trim() ||
            this.authStore.getClientName()?.trim() ||
            this.tenantStateService.tenantName()?.trim() ||
            '';
        const requestTenantId = req.headers.get('x-tenant-id')?.trim() || '';
        const tenantId = this.resolveTenantId(settings?.user?.clientId, requestTenantId);
        const userName =
            this.authStore.getUserId()?.trim() ||
            settings?.user?.subject?.trim() ||
            '';
        const accessToken = this.authStore.userInfoData().accessToken?.trim() || '';

        if (!clientName || !tenantId || !userName) {
            this.loggingService.warn('[PythonApiHeadersInterceptor] Missing one or more required Python headers.', {
                url: req.url,
                hasClientName: !!clientName,
                hasTenantId: !!tenantId,
                hasUser: !!userName,
            });
        }

        const headers = req.headers
            .set('x-Client-Name', clientName)
            .set('x-tenant-id', tenantId)
            .set('x-user', userName);

        /**
         * Attach Bearer token for Python endpoints when available.
         * Keep request unchanged if token is not present to avoid injecting
         * malformed Authorization headers.
         */
        // AuthInterceptor runs before this interceptor and may have just
        // renewed the token. Preserve that request-scoped Authorization header;
        // replacing it with AuthStore's older snapshot reintroduces the expiry
        // race this interceptor is meant to avoid.
        const requestAuthorization = req.headers.get('Authorization')?.trim();
        const finalHeaders = requestAuthorization
            ? headers
            : accessToken
              ? headers.set('Authorization', `Bearer ${accessToken}`)
              : headers;

        return next.handle(req.clone({ headers: finalHeaders }));
    }

    private resolveTenantId(settingsTenantId?: string, requestTenantId = ''): string {
        const tenantId =
            this.tenantStateService.getCurrentTenantId()?.trim() ||
            settingsTenantId?.trim() ||
            requestTenantId.trim() ||
            '';

        return tenantId.toLowerCase() === 'system' ? '' : tenantId;
    }
}
